import type { z } from 'zod'

// A setting with layered defaults: an entity's own value, else the nearest
// default above it (its tenant's, the platform's, a built-in one). The record
// stores only its own value, null for "Use default"; the backend resolves the
// rest (apps/backend/src/domain/inheritance.ts).

/** the output field holding what a field's null resolves to */
export type InheritedKey<Key extends string> = `inherited${Capitalize<Key>}`

/** the output fields of an inheritable setting: see `inheritableFields` */
export type InheritableFields<
  Key extends string,
  Value extends z.ZodType,
  Inherited extends z.ZodType,
> = { [Field in Key]: z.ZodNullable<Value> } & {
  [Field in InheritedKey<Key>]: Inherited
}

export function inheritedKey<Key extends string>(key: Key): InheritedKey<Key> {
  // Capitalize<Key> is not provable from string methods
  return `inherited${key.charAt(0).toUpperCase()}${key.slice(1)}` as InheritedKey<Key>
}

/**
 * Output fields of an inheritable setting, spread into `z.object`: `<key>` is
 * the record's own value (null: it uses the default), `inherited<Key>` what
 * null resolves to, never where from. `inherited` defaults to `value`; pass
 * e.g. `value.nullable()` when the chain can end without a value.
 */
export function inheritableFields<
  Key extends string,
  Value extends z.ZodType,
  Inherited extends z.ZodType = Value,
>(
  key: Key,
  value: Value,
  inherited?: Inherited,
): InheritableFields<Key, Value, Inherited> {
  // computed keys widen to string; the mapped type is what they are
  return {
    [key]: value.nullable(),
    [inheritedKey(key)]: inherited ?? value,
  } as InheritableFields<Key, Value, Inherited>
}

/** Input of an inheritable setting: omitted = unchanged, null = back to the default. */
export function inheritableInput<Value extends z.ZodType>(
  value: Value,
): z.ZodOptional<z.ZodNullable<Value>> {
  return value.nullable().optional()
}
